import { CheckRounded, ContentCopyRounded } from '@mui/icons-material'
import { Box, IconButton, Typography } from '@mui/material'
import React from 'react'

import SrpCouponIcon from '@/src/assets/svgs/SrpCouponIcon'
import SrpOfferDecorIcon from '@/src/assets/svgs/SrpOfferDecorIcon'

interface FlightDetailsOfferItem {
    id: string
    heading: string
    description: string
    code: string
    targetLink: string
}

interface FlightDetailsOffersProps {
    sectionTitle: string
    sectionSubtitle: string
    knowMoreLabel: string
    offers: FlightDetailsOfferItem[]
    copiedOfferId: string | null
    onCopyCode: (offerId: string, code: string) => void
    onKnowMore: (offerId: string) => void
    containerRef?: React.RefObject<HTMLDivElement | null>
}

const FlightDetailsOffers: React.FC<FlightDetailsOffersProps> = ({
    sectionTitle,
    sectionSubtitle,
    knowMoreLabel,
    offers,
    copiedOfferId,
    onCopyCode,
    onKnowMore,
    containerRef
}) => {
    if (!offers.length) return null

    return (
        <Box ref={containerRef} sx={{ mt: 2, mb: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        background: 'linear-gradient(90deg, #FDF2DC 0%, #FFFFFF 100%)',
                        borderRadius: '12px',
                        px: 1,
                        py: 1,
                        position: 'relative',
                        overflow: 'hidden'
                    }}
                >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'transparent',
                        flexShrink: 0
                    }}
                >
                    <SrpCouponIcon sx={{ fontSize: 32 }} />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                        {sectionTitle}
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 500, color: 'text.secondary', lineHeight: 1.25 }}>
                        {sectionSubtitle}
                    </Typography>
                </Box>
                <SrpOfferDecorIcon
                    sx={{
                        position: 'absolute',
                        right: 10,
                        top: '70%',
                        transform: 'translateY(-50%)',
                        width: 68,
                        height: 68,
                        color: 'transparent',
                        pointerEvents: 'none'
                    }}
                />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {offers.map(offer => (
                    <Box
                        key={offer.id}
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            p: 2,
                            bgcolor: 'background.paper'
                        }}
                    >
                        <Typography
                            sx={{
                                color: 'success.main',
                                fontSize: 16,
                                lineHeight: 1.2,
                                fontWeight: 700,
                                mb: 1
                            }}
                        >
                            {offer.heading}
                        </Typography>

                        <Typography
                            sx={{
                                color: 'text.secondary',
                                fontSize: 14,
                                lineHeight: 1.35,
                                fontWeight: 500,
                                mb: 2
                            }}
                        >
                            {offer.description}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Box
                                sx={{
                                    border: '1px dashed',
                                    borderColor: 'divider',
                                    borderRadius: 1.5,
                                    px: 1.5,
                                    py: 1,
                                    minWidth: 106,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.75
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 16,
                                        fontWeight: 600,
                                        lineHeight: 1.2,
                                        color: 'text.primary'
                                    }}
                                >
                                    {offer.code}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => onCopyCode(offer.id, offer.code)}
                                    sx={{ p: 0.25, color: 'text.primary' }}
                                >
                                    {copiedOfferId === offer.id ? (
                                        <CheckRounded sx={{ fontSize: 18 }} />
                                    ) : (
                                        <ContentCopyRounded sx={{ fontSize: 18 }} />
                                    )}
                                </IconButton>
                            </Box>

                            {offer.targetLink && (
                                <Box
                                    onClick={() => onKnowMore(offer.id)}
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        color: 'text.secondary'
                                    }}
                                >
                                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'inherit' }}>
                                        {knowMoreLabel}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                ))}
            </Box>
            </Box>
        </Box>
    )
}

export { FlightDetailsOffers }
export type { FlightDetailsOfferItem, FlightDetailsOffersProps }

